import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { X } from "lucide-react";

/**
 * AdSlot — affiche une pub active à l'emplacement donné, avec ciblage rubrique optionnel.
 * placement: banner | sidebar | explore | artist | track | footer | chat | popup
 * category (optionnel): music | humor | dance | food | art | fashion | cinema | other
 *   → filtre les pubs liées à cette rubrique (les pubs sans catégorie sont aussi montrées en fallback)
 * country (optionnel): "Madagascar" | "France" | etc.
 */
export const AdSlot = ({ placement, category, country, className = "" }) => {
  const [ad, setAd] = useState(null);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const params = { placement, active_only: true };
    if (category) params.category = category;
    if (country) params.country = country;
    api.get("/ads", { params })
      .then(({ data }) => {
        if (!data.length) return;
        const pick = data[Math.floor(Math.random() * data.length)];
        setAd(pick);
        api.post(`/ads/${pick.ad_id}/impression`).catch(() => {});
      })
      .catch(() => {});
  }, [placement, category, country]);

  if (!ad || closed) return null;

  const click = () => {
    api.post(`/ads/${ad.ad_id}/click`).catch(() => {});
    if (ad.link_url) window.open(ad.link_url, "_blank", "noopener");
  };

  const isPopup = placement === "popup";
  const content = (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-brand-red/10 via-brand-orange/5 to-brand-yellow/10 hover:border-brand-orange/40 transition-colors ${className}`}
      onClick={click}
      role={ad.link_url ? "button" : undefined}
      data-testid={`ad-slot-${placement}`}
    >
      {ad.image_url && (
        <div className="aspect-[3/1] w-full overflow-hidden bg-black/30">
          <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
      )}
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[9px] uppercase tracking-[0.2em] text-brand-yellow font-bold">Sponsorisé · {ad.advertiser_name}</span>
          {isPopup && (
            <button onClick={(e) => { e.stopPropagation(); setClosed(true); }} className="text-white/50 hover:text-white" aria-label="Fermer">
              <X size={14} />
            </button>
          )}
        </div>
        <p className="font-display text-lg md:text-xl font-black mt-1">{ad.title}</p>
        {ad.description && <p className="text-xs text-white/65 mt-1 line-clamp-2">{ad.description}</p>}
      </div>
    </div>
  );

  if (isPopup) {
    return (
      <div className="fixed bottom-6 right-6 z-40 w-[320px] max-w-[calc(100vw-48px)] shadow-2xl" data-testid="ad-popup">
        {content}
      </div>
    );
  }
  return content;
};

export default AdSlot;
